"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ManagerAssistantColumn = void 0;
const react_1 = __importDefault(require("react"));
const classnames_1 = __importDefault(require("classnames"));
const react_router_1 = require("react-router");
const _ui_1 = require("@ui");
const image_1 = require("@app/image");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const manager_assistant_1 = require("@app/reducer/manager-assistant");
const manager_assistant_chat_1 = require("./manager-assistant-chat");
const use_assistant_quick_actions_1 = require("./use-assistant-quick-actions");
const use_rail_capacity_1 = require("./use-rail-capacity");
const planning_panel_1 = __importDefault(require("./planning/planning-panel"));
require("./manager-assistant-column.scss");
// The manager assistant lives in a column fixed to the right edge of the screen, mounted
// ABOVE the router's <Switch> (see app/routing/Routes.js) so it survives navigation —
// the app shell itself remounts on every route change, which would throw the conversation
// away. It can only be COLLAPSED, never closed, and collapsing is purely a CSS width
// change: the chat below stays mounted, so an in-flight agent reply keeps streaming.
const ManagerAssistantColumn = () => {
    const dispatch = (0, store_1.useRootDispatch)();
    const expanded = (0, store_1.useRootSelector)((state) => state.managerAssistant.expanded);
    const planningOpen = (0, store_1.useRootSelector)((state) => state.managerAssistant.planningOpen);
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const columnRef = react_1.default.useRef(null);
    // The same list the chip strip above the composer renders, so folding the sidebar away does
    // not mean giving the shortcuts up. Read from the shared hook rather than a second copy:
    // a rail advertising different shortcuts from the strip is a bug nobody notices until a
    // manager expands the sidebar.
    const quickActions = (0, use_assistant_quick_actions_1.useAssistantQuickActions)(workspaceId);
    // The rail advertises all of them, so how many of them there is room for stops being a
    // constant — the set is the business's own and the rail is only as tall as the viewport.
    const { railActionsRef, railCapacity } = (0, use_rail_capacity_1.useRailCapacity)();
    // The auto-planner only means anything next to the manager calendar, so the panel is hidden
    // off /schedule. Purely a render-time check — planningOpen stays in the store, so the panel
    // comes back on return and only a dismiss (or a resolved plan) actually clears it.
    const { pathname } = (0, react_router_1.useLocation)();
    const showPlanning = planningOpen && pathname.includes('/schedule');
    const toggle = () => {
        dispatch((0, manager_assistant_1.toggledManagerAssistant)());
    };
    // The prompt a rail chip asked for, on its way to the composer. This component is the
    // nearest common ancestor of the rail and the chat, so it is where the value has to live —
    // the rail cannot reach the composer itself (see the rail's own comment below). Not Redux:
    // nothing outside this column reads it, it is cleared within the same interaction, and the
    // slice next door is persisted, which this must never be.
    const [railPrompt, setRailPrompt] = react_1.default.useState(null);
    const handleRailPromptConsumed = react_1.default.useCallback(() => {
        setRailPrompt(null);
    }, []);
    // Reported UP from inside the chat, because every one of the three reasons the composer can
    // be blocked is only knowable there — an expired session, an exhausted quota (whose hook is
    // called exactly once, in the chat) and a full conversation (only readable inside the
    // runtime provider). The rail's chips go inert with the strip's; see below.
    const [isComposerBlocked, setIsComposerBlocked] = react_1.default.useState(false);
    // Expanding should leave the manager ready to type. The composer can't carry autoFocus
    // (it's mounted permanently, and collapsed, from app load) and assistant-ui's
    // ComposerRuntime exposes no focus() — so reach for the textarea once it's visible.
    // Runs after the class lands, which is what lifts `visibility: hidden` off the body.
    react_1.default.useEffect(() => {
        if (!expanded)
            return;
        columnRef.current?.querySelector('textarea')?.focus();
    }, [expanded]);
    // Intercom's launcher is fixed to the bottom-right corner, i.e. straight on top of the
    // column — the composer when expanded, the rail's shortcuts when collapsed. Shift it left
    // by the column's RENDERED width: the widths are in rem, so a hard-coded px value drifts
    // with the browser's font size. Re-measured once the width transition has settled, so
    // Intercom gets one update per toggle rather than one per animation frame.
    react_1.default.useEffect(() => {
        const column = columnRef.current;
        if (column === null)
            return;
        const adjust = () => {
            util_1.miscUtil.intercomAdjustPosition(column.offsetWidth);
        };
        const handleTransitionEnd = (e) => {
            if (e.target === column && e.propertyName === 'width')
                adjust();
        };
        adjust();
        column.addEventListener('transitionend', handleTransitionEnd);
        return () => {
            column.removeEventListener('transitionend', handleTransitionEnd);
            // Unmounted (a page without the column) — back to the default corner.
            util_1.miscUtil.intercomAdjustPosition(0);
        };
    }, []);
    return (react_1.default.createElement("div", { ref: columnRef, className: (0, classnames_1.default)('ds-ma-column', { 'is-expanded': expanded }), "data-testid": 'ai-sidebar' },
        !expanded && (
        /* The WHOLE rail expands the column, not just the button at the top (owner's review,
           2026-09-21): a 44px strip is a small enough target without asking for one icon in
           it. The button below stays for keyboard users and as the Cypress anchor, and needs
           no handler of its own — its click bubbles here. So does a rail action's, which is
           what expands the column after the action has parked its prompt; a DISABLED action
           lets the pointer through to the rail (stylesheet), so a blocked composer still
           opens on click and shows the notice that says why. */
        react_1.default.createElement("div", { className: 'ds-ma-rail', onClick: toggle },
            react_1.default.createElement("button", { type: 'button', className: 'ds-ma-rail-toggle', "data-testid": 'ai-sidebar-toggle', "aria-label": (0, util_1.t)('SHOW') ?? undefined, title: (0, util_1.t)('SHOW') ?? undefined },
                react_1.default.createElement(_ui_1.Icon, { ico: _ui_1.Icon.ICONS.layoutSidebarRightExpand, color: _ui_1.Icon.COLORS.DARK_BLUE })),
            react_1.default.createElement("span", { className: 'ds-ma-rail-separator' }),
            react_1.default.createElement("div", { className: 'ds-ma-rail-actions', ref: railActionsRef }, quickActions
                .slice(0, railCapacity)
                .map(({ id, label, prompt, ico }) => (react_1.default.createElement("button", { key: id, type: 'button', className: 'ds-ma-rail-action', 
                // Only parks the prompt: the expand is the rail's click handler, reached by
                // bubbling.
                onClick: () => {
                    setRailPrompt(prompt);
                }, 
                // Inert whenever the strip's chips are, and for the same reason: filling an
                // input that cannot send is a dead end. The rail cannot work this out for
                // itself, so the chat reports it up.
                disabled: isComposerBlocked, "aria-label": label, title: label },
                react_1.default.createElement(_ui_1.Icon, { ico: ico, color: _ui_1.Icon.COLORS.INHERIT }))))),
            react_1.default.createElement("span", { className: 'ds-ma-rail-title' }, (0, util_1.t)('ASSISTANT.title')),
            react_1.default.createElement("span", { className: 'ds-ma-rail-logo' },
                react_1.default.createElement(image_1.LogoWide, null)))),
        react_1.default.createElement("div", { className: 'ds-ma-column-body', "aria-hidden": !expanded },
            showPlanning && (react_1.default.createElement("div", { className: 'ds-ma-planning' },
                react_1.default.createElement(planning_panel_1.default, null))),
            react_1.default.createElement(manager_assistant_chat_1.ManagerAssistantChat, { onCollapse: toggle, railPrompt: railPrompt, onRailPromptConsumed: handleRailPromptConsumed, onComposerBlockedChange: setIsComposerBlocked, isPlanningVisible: showPlanning }))));
};
exports.ManagerAssistantColumn = ManagerAssistantColumn;
exports.default = ManagerAssistantColumn;
